import React from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import type { WorkProduct } from "../../types";
import type { SddProgressReport } from "@/api/api";
import { getWorkProductBridgeMetadata } from "./FeatureStudioCard";
import {
    SDD_REQUIRED_WORK_PRODUCTS,
    type SddHierarchyLevel,
    type SddRequiredWorkProduct,
    type SddStageKey,
    findRequiredWorkProductSource,
    getSddWorkProductDescription,
    isFirstDraftReady
} from "./sddWorkflow";

type ScopeKey = "PRODUCT" | "FEATURE" | "USERSTORY";
type GenerationScope = { level: ScopeKey; featureId: number | null; userStoryId: string | null };
type WorkProductUiState = { state: "idle" | "generating" | "ready"; step?: string; progress?: number | null };

const TEMPORARILY_UNAVAILABLE_REQUIRED_VARIABLES = new Set(["security-architecture-document"]);

const levelMeta: Record<SddHierarchyLevel, { label: string; color: string }> = {
    product: { label: "Product", color: "#103699" },
    feature: { label: "Feature", color: "#0284c7" },
    story: { label: "Story", color: "#059669" }
};

const LAPTOP_FIT_QUERY = "@media (min-width: 1280px) and (min-height: 768px)";
// Keep the paired lists side by side on laptop-sized consoles; stack only when
// each column would have less than roughly 250px of usable width.
export const SDD_WORK_PRODUCT_STACK_QUERY = "@container (max-width: 520px)";
const SDD_STACKED_WORK_PRODUCT_ROW_HEIGHT = 48;
const SDD_STACKED_VISIBLE_WORK_PRODUCT_ROWS = 2;
export const SDD_STACKED_WORK_PRODUCT_LIST_HEIGHT = SDD_STACKED_WORK_PRODUCT_ROW_HEIGHT * SDD_STACKED_VISIBLE_WORK_PRODUCT_ROWS;

export const workProductScrollbarSx = {
    scrollbarWidth: "thin",
    scrollbarColor: "#cbd5e1 transparent",
    scrollbarGutter: "stable",
    "&::-webkit-scrollbar": { width: 8 },
    "&::-webkit-scrollbar-track": { bgcolor: "transparent", borderRadius: 999 },
    "&::-webkit-scrollbar-thumb": {
        bgcolor: "#cbd5e1",
        border: "2px solid transparent",
        backgroundClip: "padding-box",
        borderRadius: 999
    },
    "&:hover": { scrollbarColor: "#94a3b8 #f1f5f9" },
    "&:hover::-webkit-scrollbar-track": { bgcolor: "#f1f5f9" },
    "&:hover::-webkit-scrollbar-thumb": { bgcolor: "#94a3b8" }
} as const;

export function WorkProductAcceptanceButton({
    accepted,
    disabled,
    name,
    onToggle
}: {
    accepted: boolean;
    disabled: boolean;
    name: string;
    onToggle: () => unknown | Promise<unknown>;
}) {
    const [pending, setPending] = React.useState(false);
    const blocked = disabled || pending;
    const tooltip = disabled
        ? "Build this work product before accepting it"
        : pending
          ? accepted
              ? "Returning work product to review"
              : "Accepting work product"
        : accepted
          ? "Accepted — click to return to review"
          : "Accept work product";
    const handleToggle = async () => {
        if (blocked) return;
        setPending(true);
        try {
            const result = onToggle();
            if (result && typeof (result as PromiseLike<unknown>).then === "function") {
                await (result as PromiseLike<unknown>);
            }
        } finally {
            setPending(false);
        }
    };
    return (
        <Tooltip title={tooltip}>
            <span>
                <IconButton
                    size="small"
                    disabled={blocked}
                    aria-label={accepted ? `Unaccept ${name}` : `Accept ${name}`}
                    onClick={() => void handleToggle()}
                    sx={{
                        width: 26,
                        height: 26,
                        p: 0,
                        flex: "0 0 auto",
                        borderRadius: "7px",
                        border: "1px solid",
                        borderColor: accepted ? "transparent" : "rgba(0,0,0,.07)",
                        bgcolor: "transparent",
                        color: accepted ? "#059669" : "#4b5563",
                        "&:hover": { bgcolor: accepted ? "rgba(5,150,105,.05)" : "#f8fafc" },
                        "&.Mui-disabled": {
                            borderColor: "rgba(0,0,0,.07)",
                            bgcolor: "#f1f5f9",
                            color: "#e2e8f0"
                        }
                    }}
                >
                    {pending ? (
                        <CircularProgress size={12} thickness={5} color="inherit" />
                    ) : (
                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    )}
                </IconButton>
            </span>
        </Tooltip>
    );
}

function scopedIdentity(definition: SddRequiredWorkProduct, feature: any, story: any): GenerationScope {
    const featureId = Number(feature?.featureid || feature?.id || 0) || null;
    const storyId = story?.id ? String(story.id) : null;
    if (definition.level === "product") return { level: "PRODUCT", featureId: null, userStoryId: null };
    if (definition.level === "feature") return { level: "FEATURE", featureId, userStoryId: null };
    return { level: "USERSTORY", featureId, userStoryId: storyId };
}

export function SddRequiredWorkProducts({
    stage,
    sources,
    feature,
    story,
    headerActions,
    getState,
    progress,
    onGenerate,
    onOpen,
    onApprove,
    bridgePhase = "discovery"
}: {
    stage: SddStageKey;
    activeLevel: SddHierarchyLevel;
    sources: WorkProduct[];
    project: any;
    feature: any;
    story: any;
    headerActions?: React.ReactNode;
    getState: (workProduct: WorkProduct, scope?: GenerationScope) => WorkProductUiState;
    progress?: SddProgressReport | null;
    onGenerate: (workProduct: WorkProduct, scope?: GenerationScope) => void;
    onOpen: (workProduct: WorkProduct, scope?: GenerationScope) => void;
    onApprove: (workProduct: WorkProduct, approved: boolean, scope?: GenerationScope) => Promise<boolean | void> | boolean | void;
    onFeatureSelect?: (feature: any) => void;
    onStorySelect?: (story: any) => void;
    bridgeContext?: string;
    bridgePhase?: string;
}) {
    const rows = SDD_REQUIRED_WORK_PRODUCTS[stage].map(definition => {
        const source = findRequiredWorkProductSource(definition, sources);
        const scope = scopedIdentity(definition, feature, story);
        const state = source ? getState(source, scope) : { state: "idle" as const };
        const progressRow = progress?.rows.find(row =>
            row.canonical_variable === definition.variable &&
            row.level === scope.level &&
            String(row.feature_id || "") === String(scope.featureId || "") &&
            String(row.story_id || "") === String(scope.userStoryId || "")
        );
        const ready = Boolean(source && (state.state === "ready" || isFirstDraftReady(source) || progressRow?.status === "in_progress" || progressRow?.status === "accepted"));
        const accepted = progressRow ? progressRow.status === "accepted" : Boolean(source?.approved);
        const hasScope = definition.level === "product" || Boolean(scope.featureId && (definition.level !== "story" || scope.userStoryId));
        return { definition, source, state, ready, accepted, scope, hasScope };
    });
    const readyForReview = rows.filter(row => row.ready && !row.accepted).length;

    return (
        <Box
            component="section"
            aria-labelledby="required-work-products-title"
            sx={{
                minWidth: 0,
                [LAPTOP_FIT_QUERY]: { height: "100%", minHeight: 0, display: "flex", flexDirection: "column" },
                [SDD_WORK_PRODUCT_STACK_QUERY]: { height: "auto" }
            }}
        >
            <Box
                sx={{
                    minHeight: 116,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    flexShrink: 0,
                    [LAPTOP_FIT_QUERY]: { minHeight: 78 }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                    <Typography
                        id="required-work-products-title"
                        sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#475569" }}
                    >
                        Build Required Work Products for this Step
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                        {rows.length} required · {readyForReview} ready for review
                    </Typography>
                </Box>
                {headerActions}
                <Typography sx={{ mt: headerActions ? 0 : "auto", fontSize: 12, lineHeight: 1.5, color: "#64748b" }}>
                    Generate, review and edit required work products.
                </Typography>
            </Box>
            <Box
                data-testid="required-work-products-list"
                sx={{
                    height: 300,
                    overflowY: "auto",
                    pr: 0.5,
                    ...workProductScrollbarSx,
                    [LAPTOP_FIT_QUERY]: { height: "auto", flex: 1, minHeight: 0 },
                    [SDD_WORK_PRODUCT_STACK_QUERY]: {
                        height: SDD_STACKED_WORK_PRODUCT_LIST_HEIGHT,
                        minHeight: SDD_STACKED_WORK_PRODUCT_LIST_HEIGHT,
                        flex: "0 0 auto"
                    }
                }}
            >
                {rows.map(row => (
                    <RequiredWorkProductRow
                        key={`${row.definition.level}-${row.definition.name}`}
                        {...row}
                        bridgePhase={bridgePhase}
                        onGenerate={onGenerate}
                        onOpen={onOpen}
                        onApprove={onApprove}
                    />
                ))}
            </Box>
        </Box>
    );
}

function RequiredWorkProductRow({
    definition,
    source,
    state,
    ready,
    accepted,
    scope,
    hasScope,
    onGenerate,
    onOpen,
    onApprove,
    bridgePhase
}: {
    definition: SddRequiredWorkProduct;
    source?: WorkProduct;
    state: WorkProductUiState;
    ready: boolean;
    accepted: boolean;
    scope: GenerationScope;
    hasScope: boolean;
    onGenerate: (workProduct: WorkProduct, scope?: GenerationScope) => void;
    onOpen: (workProduct: WorkProduct, scope?: GenerationScope) => void;
    onApprove: (workProduct: WorkProduct, approved: boolean, scope?: GenerationScope) => Promise<boolean | void> | boolean | void;
    bridgePhase: string;
}) {
    const isTemporarilyUnavailable = Boolean(definition.variable) && TEMPORARILY_UNAVAILABLE_REQUIRED_VARIABLES.has(definition.variable);
    const generating = state.state === "generating";
    const actionable = Boolean(source && hasScope && !generating && !isTemporarilyUnavailable);
    const level = levelMeta[definition.level];
    const context = scope.level === "USERSTORY" ? `userstory-${scope.userStoryId}` : scope.level === "FEATURE" ? `feature-${scope.featureId}` : "product";
    const bridge =
        source && hasScope
            ? getWorkProductBridgeMetadata({
                  workProductId: String(source.id),
                  workProductName: source.name,
                  state: generating ? "generating" : ready ? "ready" : "idle",
                  context,
                  phase: bridgePhase
              })
            : null;
    const actionLabel = isTemporarilyUnavailable
        ? "Unavailable"
        : generating
          ? "Building…"
          : ready
            ? "View/Edit"
            : source && hasScope
              ? "Build"
              : "Unavailable";
    const unavailableTooltip = isTemporarilyUnavailable
        ? "This work product is temporarily unavailable."
        : !hasScope
        ? definition.level === "feature"
            ? "Choose a Feature to build this work product."
            : scope.featureId
              ? "Choose a User Story to build this work product."
              : "Choose a Feature and User Story to build this work product."
        : !source
          ? "This work product is unavailable for the current selection."
          : "";

    return (
        <Box
            data-required-work-product-card
            data-ui-bridge={bridge?.elementId}
            data-ui-label={bridge?.uiLabel}
            data-ui-role={bridge?.uiRole}
            data-ui-group={bridge?.uiGroup}
            data-ui-description={bridge?.uiDescription}
            data-work-product-id={source ? String(source.id) : undefined}
            data-work-product-name={source?.name}
            data-work-product-level={scope.level}
            data-work-product-feature-id={scope.featureId ?? undefined}
            data-work-product-user-story-id={scope.userStoryId ?? undefined}
            sx={{
                minHeight: definition.level === "story" ? 38 : 44,
                display: "grid",
                gridTemplateColumns: "18px 56px minmax(0,1fr) auto 34px",
                alignItems: "center",
                gap: 0.75,
                borderBottom: "1px solid #e5e7eb",
                py: definition.level === "story" ? 0.35 : 0.7
            }}
        >
            <Tooltip title={getSddWorkProductDescription(source)}>
                <InfoOutlined aria-label={`About ${definition.name}`} sx={{ fontSize: 15, color: "#94a3b8" }} />
            </Tooltip>
            <Typography sx={{ position: "relative", top: 2, fontSize: 10.5, lineHeight: 1.6, fontWeight: 700, color: level.color }}>{level.label}</Typography>
            <Typography noWrap title={definition.name} sx={{ position: "relative", top: 2, fontSize: 12.5, lineHeight: 1.6, fontWeight: 600, color: "#0f172a" }}>
                {definition.name}
            </Typography>
            <Tooltip title={unavailableTooltip} disableHoverListener={!unavailableTooltip}>
                <span style={{ display: "inline-flex" }}>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!actionable}
                        startIcon={generating ? <CircularProgress size={11} color="inherit" /> : undefined}
                        aria-label={`${actionLabel === "Unavailable" ? "Unavailable" : generating ? "Generating" : ready ? "Open" : "Generate"} ${definition.name}`}
                        onClick={() => {
                            if (!source || !hasScope || generating) return;
                            if (ready) onOpen(source, scope);
                            else onGenerate(source, scope);
                        }}
                        sx={{ minWidth: 76, height: 28, px: 1, fontSize: 10.5, textTransform: "none" }}
                    >
                        {actionLabel}
                    </Button>
                </span>
            </Tooltip>
            <WorkProductAcceptanceButton
                accepted={accepted}
                disabled={isTemporarilyUnavailable || !source || !hasScope || !ready || generating}
                name={definition.name}
                onToggle={() => source && onApprove(source, !accepted, scope)}
            />
        </Box>
    );
}
